import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import FormCheckbox from "@ats/src/components/forms/FormCheckbox";
import FormContainer from "@ats/src/components/forms/FormContainer";
import Icon from "@ats/src/components/shared/Icon";

import { useModalContext } from "@shared/context/ModalContext";
import { useToastContext } from "@shared/context/ToastContext";
import { useOrganizationAiCreditBalance } from "@shared/queryHooks/useOrganizationAiCreditBalance";
import { useBulkGenerateAllStagesAiSummaries } from "@shared/queryHooks/useBulkGenerateAiSummaries";
import { validateBulkGenerateAiSummaries } from "@shared/lib/validateWithYup";
import { trackEvent } from "@shared/lib/posthog";

interface Props {
  jobId: number;
  candidatesCount: number;
  summaryCount: number;
  onCancel: () => void;
}

function RunPlatoReviewAllModal({
  jobId,
  candidatesCount,
  summaryCount,
  onCancel,
}: Props) {
  const { dismissModalWithAnimation } = useModalContext();
  const addToast = useToastContext();
  const { mutate: bulkGenerate, isLoading } = useBulkGenerateAllStagesAiSummaries();

  const [rescore, setRescore] = React.useState(false);
  const [errors, setErrors] = React.useState([]);

  const { data, isError } = useOrganizationAiCreditBalance();
  const available = isError ? 0 : data?.totalCreditsRemaining || 0;
  const candidatesToScoreCount = rescore ? candidatesCount : Math.max(candidatesCount - summaryCount, 0);
  const shortfall = Math.max(0, candidatesToScoreCount - available);

  const handleOnCancel = () => {
    dismissModalWithAnimation(() => onCancel);
  };

  const handleOnConfirm = async (event) => {
    event.preventDefault();
    const [isValid, validationErrors] = await validateBulkGenerateAiSummaries({
      availableCredits: available,
    });
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    bulkGenerate(
      {
        jobId,
        rescoreRequested: rescore,
      },
      {
        onSuccess: (data: { queuedCount: number; skippedCount: number; anyTextractPending: boolean }) => {
          trackEvent("run_plato_review_all_confirmed", { job_id: jobId, candidates_count: candidatesCount });
          const parts = [];
          if (data.queuedCount > 0) {
            parts.push(`Queued ${data.queuedCount} review${data.queuedCount === 1 ? "" : "s"}`);
          }
          if (data.skippedCount > 0) {
            parts.push(`${data.skippedCount} skipped (no resume, already processing, or not enough credits)`);
          }
          if (data.anyTextractPending) {
            parts.push("some resumes are still processing");
          }
          const title = parts.length > 0 ? parts.join(". ") : "No reviews to generate";
          addToast({ title, kind: "success" });
          dismissModalWithAnimation(() => onCancel);
        },
        onError: (error: any) => {
          addToast({
            title: error?.data?.errors?.general?.[0] || "Failed to queue reviews",
            kind: "warning",
            delay: 10000,
          });
        },
      },
    );
  };

  const modalButtons = (
    <Styled.ButtonContainer>
      <Button
        className="submit-button"
        type="submit"
        size="medium"
        onClick={handleOnConfirm}
        loading={isLoading}
        disabled={isLoading || candidatesToScoreCount === 0}
        aria-label="Review all candidates"
      >
        Review all
      </Button>
      <Button
        styleType="secondary"
        onClick={(event) => {
          if (event) event.preventDefault();
          handleOnCancel();
        }}
      >
        Cancel
      </Button>
    </Styled.ButtonContainer>
  );

  return (
    <CenterModal headerTitleText="Review all candidates" onCancel={handleOnCancel}>
      <Styled.Body>
        {candidatesToScoreCount} candidate{candidatesToScoreCount === 1 ? "" : "s"} in this job
        {candidatesToScoreCount === 1 ? " doesn't" : " don't"} have a Plato review yet.
        Each successful review uses one AI credit
        {shortfall > 0 && candidatesToScoreCount > 0 ? (
          <>
            . You are short <b>{shortfall}</b> credit{shortfall === 1 ? "" : "s"}. The first{" "}
            {available} candidate{available === 1 ? "" : "s"} will get reviews generated; the rest
            will be skipped.
          </>
        ) : (
          <>
            , so running this uses up to <b>{candidatesToScoreCount}</b> credit
            {candidatesToScoreCount === 1 ? "" : "s"} from your balance of <b>{available}</b> available.
          </>
        )}
      </Styled.Body>

      <Styled.RescoreCheckbox>
        <FormCheckbox
          name="rescore"
          label="Also re-review candidates that already have a review"
          description="Recommended if you've changed the candidate requirements in the job description."
          checked={rescore}
          onChange={() => setRescore((currentRescore) => !currentRescore)}
        />
      </Styled.RescoreCheckbox>

      <Styled.Statement>
        <Icon name="mail" />
        <span>
          The hiring team gets an email with the final count when it's done. Candidates without a
          resume, or one that's still processing, are skipped.
        </span>
      </Styled.Statement>

      <FormContainer errors={errors} buttons={modalButtons} onSubmit={handleOnConfirm} />
    </CenterModal>
  );
}

RunPlatoReviewAllModal.propTypes = {};
RunPlatoReviewAllModal.defaultProps = {};

export default RunPlatoReviewAllModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Body = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoReviewAllModal_Body;
    ${t.mt(2)}
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    line-height: 1.5;
  `;
});

Styled.RescoreCheckbox = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoReviewAllModal_RescoreCheckbox;
    ${t.mt(4)}
  `;
});

Styled.Statement = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoReviewAllModal_Statement;
    ${[t.mt(4), t.p(3), t.rounded.md]}
    display: flex;
    gap: 0.625rem;
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
    line-height: 1.5;
    ${t.text.sm}

    svg {
      flex-shrink: 0;
      margin-top: 0.0625rem;
      width: 1rem;
      height: 1rem;
      color: ${t.dark ? t.color.gray[500] : t.color.gray[400]};
    }
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoReviewAllModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});
